const BASE_URL = import.meta.env.DEV
  ? 'http://64.23.188.15:8000/api/v1'
  : '/api/v1';

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && token !== 'undefined' ? { 'Authorization': `Bearer ${token.replace(/"/g, '')}` } : {}),
    },
    cache: 'no-store',
    credentials: 'omit',
    mode: 'cors',
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  const response = await fetch(url, mergedOptions);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

// ==================== INTERFACES ====================

export interface Section {
  _id: string;
  name: string;
  description?: string;
  code?: string;
  active: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Option {
  _id: string;
  name: string;
  code: string;
  sectionId: string;
  description?: string;
  active: boolean;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Classroom {
  _id: string;
  companyId: string;
  name: string;
  level: string;
  capacity?: number;
  schoolYear: string;
  sectionId?: Section | string;
  optionId?: Option | string;
  description?: string;
  studentCount?: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Student {
  _id: string;
  companyId: string;
  matricule: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  gender: 'M' | 'F';
  birthDate?: string;
  classroomId: string | Classroom;
  schoolYear: string;
  tuteur?: {
    name?: string;
    phone?: string;
  };
  status: 'actif' | 'transfert' | 'sorti';
  createdAt: string;
  updatedAt: string;
}

export interface Teacher {
  _id: string;
  companyId: string;
  code?: string;
  firstName?: string;
  lastName: string;
  phone?: string;
  email?: string;
  classes?: (string | Classroom)[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SchoolFee {
  _id: string;
  companyId: string;
  label: string;
  periodicity: 'unique' | 'mensuel' | 'trimestriel';
  schoolYear: string;
  currency?: string;
  amount: number;
  classroomIds: (string | Classroom)[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  _id: string;
  companyId: string;
  studentId: string | Student;
  schoolFeeId: string | SchoolFee;
  amount: number;
  paymentDate: string;
  paymentMethod: 'cash' | 'mobile_money' | 'bank_transfer' | 'check';
  reference?: string;
  notes?: string;
  status: 'completed' | 'partial' | 'pending';
  recordedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalPages: number;
  currentPage: number;
  total: number;
}

// ==================== SECTIONS API ====================

export const sectionsApi = {
  async getAll(): Promise<Section[]> {
    const response = await fetchWithAuth(`${BASE_URL}/sections`);
    return response.sections || response;
  },

  async getById(id: string): Promise<Section> {
    const response = await fetchWithAuth(`${BASE_URL}/sections/${id}`);
    return response.section || response;
  },

  async create(data: Partial<Section>): Promise<Section> {
    const response = await fetchWithAuth(`${BASE_URL}/sections`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.section || response;
  },

  async update(id: string, data: Partial<Section>): Promise<Section> {
    const response = await fetchWithAuth(`${BASE_URL}/sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.section || response;
  },

  async delete(id: string): Promise<void> {
    await fetchWithAuth(`${BASE_URL}/sections/${id}`, {
      method: 'DELETE',
    });
  },

  async getActive(): Promise<Section[]> {
    const response = await fetchWithAuth(`${BASE_URL}/sections/active`);
    return response.sections || response.data || response;
  },

  async toggle(id: string): Promise<Section> {
    const response = await fetchWithAuth(`${BASE_URL}/sections/${id}/toggle`, {
      method: 'PATCH',
    });
    return response.data || response.section || response;
  },

  async getByCompany(companyId: string): Promise<Section[]> {
    const response = await fetchWithAuth(`${BASE_URL}/sections/company/${companyId}`);
    return response.sections || response.data || response;
  },
};

// ==================== OPTIONS API ====================

export const optionsApi = {
  async getAll(sectionId?: string): Promise<Option[]> {
    const url = sectionId ? `${BASE_URL}/options?sectionId=${sectionId}` : `${BASE_URL}/options`;
    const response = await fetchWithAuth(url);
    return response.options || response;
  },

  async getById(id: string): Promise<Option> {
    const response = await fetchWithAuth(`${BASE_URL}/options/${id}`);
    return response.option || response;
  },

  async create(data: Partial<Option>): Promise<Option> {
    const response = await fetchWithAuth(`${BASE_URL}/options`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.option || response;
  },

  async update(id: string, data: Partial<Option>): Promise<Option> {
    const response = await fetchWithAuth(`${BASE_URL}/options/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.option || response;
  },

  async delete(id: string): Promise<void> {
    await fetchWithAuth(`${BASE_URL}/options/${id}`, {
      method: 'DELETE',
    });
  },

  async getActive(): Promise<Option[]> {
    const response = await fetchWithAuth(`${BASE_URL}/options/active`);
    return response.options || response.data || response;
  },

  async toggle(id: string): Promise<Option> {
    const response = await fetchWithAuth(`${BASE_URL}/options/${id}/toggle`, {
      method: 'PATCH',
    });
    return response.data || response.option || response;
  },

  async getByCompany(companyId: string): Promise<Option[]> {
    const response = await fetchWithAuth(`${BASE_URL}/options/company/${companyId}`);
    return response.options || response.data || response;
  },
};

// ==================== CLASSROOMS API ====================

export const classroomsApi = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    level?: string;
    schoolYear?: string;
    sectionId?: string;
    active?: boolean;
    name?: string;
  }): Promise<PaginatedResponse<Classroom>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.level) queryParams.append('level', params.level);
    if (params?.schoolYear) queryParams.append('schoolYear', params.schoolYear);
    if (params?.sectionId) queryParams.append('sectionId', params.sectionId);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());
    if (params?.name) queryParams.append('name', params.name);

    const url = `${BASE_URL}/classrooms${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    
    return {
      data: response.classrooms || [],
      totalPages: response.pagination?.pages || 1,
      currentPage: response.pagination?.page || 1,
      total: response.pagination?.total || 0,
    };
  },

  async getById(id: string): Promise<Classroom> {
    const response = await fetchWithAuth(`${BASE_URL}/classrooms/${id}`);
    return response.classroom || response;
  },

  async create(data: Partial<Classroom>): Promise<Classroom> {
    const response = await fetchWithAuth(`${BASE_URL}/classrooms`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.classroom || response;
  },

  async update(id: string, data: Partial<Classroom>): Promise<Classroom> {
    const response = await fetchWithAuth(`${BASE_URL}/classrooms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.classroom || response;
  },

  async delete(id: string): Promise<void> {
    await fetchWithAuth(`${BASE_URL}/classrooms/${id}`, {
      method: 'DELETE',
    });
  },

  async toggleActive(id: string): Promise<Classroom> {
    const response = await fetchWithAuth(`${BASE_URL}/classrooms/${id}/toggle-active`, {
      method: 'PATCH',
    });
    return response.classroom || response;
  },
};

// ==================== STUDENTS API ====================

export const studentsApi = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    classroomId?: string;
    schoolYear?: string;
    search?: string;
    gender?: 'M' | 'F';
    status?: 'actif' | 'transfert' | 'sorti';
  }): Promise<PaginatedResponse<Student>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.classroomId) queryParams.append('classroomId', params.classroomId);
    if (params?.schoolYear) queryParams.append('schoolYear', params.schoolYear);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.gender) queryParams.append('gender', params.gender);
    if (params?.status) queryParams.append('status', params.status);

    const url = `${BASE_URL}/students${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    
    return {
      data: response.students || [],
      totalPages: response.pagination?.pages || 1,
      currentPage: response.pagination?.page || 1,
      total: response.pagination?.total || 0,
    };
  },

  async getById(id: string): Promise<Student> {
    const response = await fetchWithAuth(`${BASE_URL}/students/${id}`);
    return response.student || response;
  },

  async create(data: Partial<Student>): Promise<Student> {
    const response = await fetchWithAuth(`${BASE_URL}/students`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.student || response;
  },

  async update(id: string, data: Partial<Student>): Promise<Student> {
    const response = await fetchWithAuth(`${BASE_URL}/students/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.student || response;
  },

  async delete(id: string): Promise<void> {
    await fetchWithAuth(`${BASE_URL}/students/${id}`, {
      method: 'DELETE',
    });
  },

  async transfer(id: string, classroomId: string, schoolYear?: string): Promise<Student> {
    const body: any = { classroomId };
    if (schoolYear) body.schoolYear = schoolYear;
    const response = await fetchWithAuth(`${BASE_URL}/students/${id}/move`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.student || response;
  },

  async getByClassroom(classroomId: string, params?: {
    status?: string;
    q?: string;
    page?: number;
    limit?: number;
  }): Promise<{ classroom: any; students: Student[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.q) queryParams.append('q', params.q);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const url = `${BASE_URL}/students/classroom/${classroomId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },
};

// ==================== TEACHERS API ====================

export const teachersApi = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
    classroomId?: string;
  }): Promise<PaginatedResponse<Teacher>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());
    if (params?.classroomId) queryParams.append('classroomId', params.classroomId);

    const url = `${BASE_URL}/teachers${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    
    return {
      data: response.teachers || [],
      totalPages: response.pagination?.pages || 1,
      currentPage: response.pagination?.page || 1,
      total: response.pagination?.total || 0,
    };
  },

  async getById(id: string): Promise<Teacher> {
    const response = await fetchWithAuth(`${BASE_URL}/teachers/${id}`);
    return response.teacher || response;
  },

  async create(data: Partial<Teacher>): Promise<Teacher> {
    const response = await fetchWithAuth(`${BASE_URL}/teachers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.teacher || response;
  },

  async update(id: string, data: Partial<Teacher>): Promise<Teacher> {
    const response = await fetchWithAuth(`${BASE_URL}/teachers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.teacher || response;
  },

  async delete(id: string): Promise<void> {
    await fetchWithAuth(`${BASE_URL}/teachers/${id}`, {
      method: 'DELETE',
    });
  },
};

// ==================== SCHOOL FEES API ====================

export const schoolFeesApi = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    periodicity?: string;
    schoolYear?: string;
    classroomId?: string;
    active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<SchoolFee>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.periodicity) queryParams.append('periodicity', params.periodicity);
    if (params?.schoolYear) queryParams.append('schoolYear', params.schoolYear);
    if (params?.classroomId) queryParams.append('classroomId', params.classroomId);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());
    if (params?.search) queryParams.append('search', params.search);

    const url = `${BASE_URL}/school-fees${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    
    return {
      data: response.schoolFees || [],
      totalPages: response.pagination?.pages || 1,
      currentPage: response.pagination?.page || 1,
      total: response.pagination?.total || 0,
    };
  },

  async getById(id: string): Promise<SchoolFee> {
    const response = await fetchWithAuth(`${BASE_URL}/school-fees/${id}`);
    return response.schoolFee || response.fee || response;
  },

  async create(data: Partial<SchoolFee>): Promise<SchoolFee> {
    const response = await fetchWithAuth(`${BASE_URL}/school-fees`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.schoolFee || response.fee || response;
  },

  async update(id: string, data: Partial<SchoolFee>): Promise<SchoolFee> {
    const response = await fetchWithAuth(`${BASE_URL}/school-fees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.schoolFee || response.fee || response;
  },

  async delete(id: string): Promise<void> {
    await fetchWithAuth(`${BASE_URL}/school-fees/${id}`, {
      method: 'DELETE',
    });
  },

  async toggleActive(id: string): Promise<SchoolFee> {
    const response = await fetchWithAuth(`${BASE_URL}/school-fees/${id}/active`, {
      method: 'PATCH',
    });
    return response.schoolFee || response;
  },
};

// ==================== PAYMENTS API ====================
// Documentation complète: Tous les endpoints paiements disponibles

export const paymentsApi = {
  // Créer un paiement
  async create(data: Partial<Payment>): Promise<Payment> {
    const response = await fetchWithAuth(`${BASE_URL}/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.payment || response;
  },

  // Historique des paiements d'un étudiant (avec pagination)
  async getByStudent(studentId: string, params?: {
    page?: number;
    limit?: number;
    schoolFeeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ student: any; payments: Payment[]; summary: any; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.schoolFeeId) queryParams.append('schoolFeeId', params.schoolFeeId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `${BASE_URL}/payments/student/${studentId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Statut de paiement d'un étudiant pour un frais spécifique
  async getPaymentStatus(studentId: string, schoolFeeId: string): Promise<any> {
    const response = await fetchWithAuth(`${BASE_URL}/payments/status/${studentId}/${schoolFeeId}`);
    return response;
  },

  // Paiements d'une classe pour un frais spécifique
  async getClassroomPayments(classroomId: string, schoolFeeId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);

    const url = `${BASE_URL}/payments/classroom/${classroomId}/${schoolFeeId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Élèves ayant payé un frais dans une classe
  async getPaidStudents(classroomId: string, schoolFeeId: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = `${BASE_URL}/payments/paid-students/${classroomId}/${schoolFeeId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Paiements récents
  async getRecent(params?: {
    limit?: number;
    page?: number;
  }): Promise<{ payments: Payment[]; summary: any; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());

    const url = `${BASE_URL}/payments/recent${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Élèves ayant complètement payé un frais
  async getFullyPaid(schoolFeeId: string, params?: {
    page?: number;
    limit?: number;
    classroomId?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.classroomId) queryParams.append('classroomId', params.classroomId);

    const url = `${BASE_URL}/payments/fully-paid/${schoolFeeId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Élèves ayant payé plus qu'un montant
  async getAboveAmount(schoolFeeId: string, minAmount: number, params?: {
    page?: number;
    limit?: number;
    classroomId?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('minAmount', minAmount.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.classroomId) queryParams.append('classroomId', params.classroomId);

    const url = `${BASE_URL}/payments/above-amount/${schoolFeeId}?${queryParams.toString()}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Élèves n'ayant pas payé un frais
  async getUnpaid(schoolFeeId: string, params?: {
    page?: number;
    limit?: number;
    classroomId?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.classroomId) queryParams.append('classroomId', params.classroomId);

    const url = `${BASE_URL}/payments/unpaid/${schoolFeeId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Statistiques globales de paiements
  async getStatistics(params?: {
    period?: 'current' | 'lastMonth' | 'lastYear';
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.period) queryParams.append('period', params.period);

    const url = `${BASE_URL}/payments/statistics${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url);
    return response;
  },

  // Récupérer un paiement par ID
  async getById(id: string): Promise<Payment> {
    const response = await fetchWithAuth(`${BASE_URL}/payments/${id}`);
    return response.payment || response;
  },

  // Mettre à jour un paiement
  async update(id: string, data: Partial<Payment>): Promise<Payment> {
    const response = await fetchWithAuth(`${BASE_URL}/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.payment || response;
  },

  // Annuler un paiement
  async cancel(id: string, reason?: string): Promise<Payment> {
    const response = await fetchWithAuth(`${BASE_URL}/payments/${id}/cancel`, {
      method: 'PATCH',
      body: reason ? JSON.stringify({ reason }) : undefined,
    });
    return response.payment || response;
  },
};

// ==================== DASHBOARD ====================

export interface DashboardStats {
  overview: {
    totalStudents: number;
    totalClassrooms: number;
    totalTeachers: number;
    totalSchoolFees: number;
    paymentsThisMonth: number;
    totalAmountThisMonth: number;
    collectionRate: number;
  };
  students: {
    total: number;
    byGender: Array<{ gender: string; count: number }>;
  };
  classrooms: {
    total: number;
    byLevel: Array<{ level: string; count: number }>;
    topClassrooms: Array<{
      name: string;
      code: string;
      level: string;
      studentCount: number;
    }>;
  };
  teachers: {
    total: number;
  };
  schoolFees: {
    total: number;
    byPeriodicity: Array<{ periodicity: string; count: number }>;
  };
  payments: {
    thisMonth: {
      count: number;
      totalAmount: number;
      byMethod: Array<{ method: string; count: number; total: number }>;
    };
    recent: Array<{
      _id: string;
      studentId: {
        matricule: string;
        firstName: string;
        lastName: string;
      };
      schoolFeeId: {
        label: string;
        amount: number;
        currency: string;
      };
      amount: number;
      paymentDate: string;
      paymentMethod: string;
      status: string;
    }>;
    financial: {
      totalExpected: number;
      totalCollected: number;
      collectionRate: number;
      outstanding: number;
    };
  };
  period: {
    month: string;
    schoolYear: string;
  };
}

export const dashboardApi = {
  async getSchoolStats(schoolYear?: string): Promise<DashboardStats> {
    const url = schoolYear 
      ? `${BASE_URL}/dashboard/school?schoolYear=${schoolYear}`
      : `${BASE_URL}/dashboard/school`;
    const response = await fetchWithAuth(url);
    return response;
  },
};

// ==================== COMMUNICATION ====================

export interface CommunicationResponse {
  message: string;
  recipient?: {
    studentName: string;
    matricule: string;
    parentName: string;
    parentPhone: string;
    classroom: string;
  };
  classroom?: {
    name: string;
    code: string;
    level: string;
  };
  summary?: {
    totalStudents: number;
    studentsWithPhone: number;
    messagesSent: number;
    messagesFailed: number;
  };
  byClass?: Record<string, { sent: number; failed: number }>;
  results?: Array<{
    studentName: string;
    matricule: string;
    classroom?: string;
    parentPhone: string;
    status: string;
  }>;
  smsResult?: any;
}

export const communicationApi = {
  // Envoyer un message à un étudiant
  async sendToStudent(studentId: string, message: string): Promise<CommunicationResponse> {
    const response = await fetchWithAuth(`${BASE_URL}/communication/student/${studentId}`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    return response;
  },

  // Envoyer un message à une classe
  async sendToClassroom(classroomId: string, message: string): Promise<CommunicationResponse> {
    const response = await fetchWithAuth(`${BASE_URL}/communication/classroom/${classroomId}`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    return response;
  },

  // Envoyer un message à toute l'école
  async sendToSchool(message: string): Promise<CommunicationResponse> {
    const response = await fetchWithAuth(`${BASE_URL}/communication/school`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    return response;
  },
};
